import { useCallback, useState } from "react";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { PrincipalDocumentosLista } from "../../../auditoria/domain/models/documentos_lista";
import { useAuditoresPagination } from "../../../auditoria/presentation/hooks/Auditoria_auditores/pagination";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { DashboardComercial } from "../../domain/models/dashboard_comercial";
import { initialNovosNegociosFiltersValues, useNovosNegociosListaFilters } from "../hooks/novos_negocios_lista/filters";
import { useClientesListaPagination } from "../hooks/clientes_lista/pagination";
import { initialClientesListaFiltersValues, useClientesListaFilters } from "../hooks/clientes_lista/filters";
import { useNovosNegociosListaPagination } from "../hooks/novos_negocios_lista/pagination";

import CommonConstants from "../../../../common/constants";

/**
 * LoadPrincipalListaFuncState
 */

type LoadPrincipalListaFuncState = {
  IdArquivoModulo?: number;
  page?: number;
  rowsPerPage?: number;
  tipoPesquisa?: number,
  pesquisa?: string,
};

/**
 * usePrincipalComercialViewModel
 */

export const usePrincipalComercialViewModel = () => {
  const {
    commons: {
      getAllListagemDocUseCase,
      getGedArquivoDataUseCase,
    },
    comercial: {
      getDashboardComercialInformationUseCase,
    },
  } = useDependencyInjection();

  const { pushErrorNotification } = usePageLoader();

  const backOrNavigate = useBackOrNavigate();

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [dashboardComercialInformation, setDashboardComercialInformation] = useState<DashboardComercial>();

  const [docLista, setDocLista] = useState<PrincipalDocumentosLista[]>([]);

  const pagination = useAuditoresPagination();

  /**
   * Funcoes de carregamento
   */

  const loadDashboardComercialInformation = useCallback(async () => {
    try {
      const result = await getDashboardComercialInformationUseCase.getDashboardComercialInformation();
      setDashboardComercialInformation(result);
    } catch (e: unknown) {
      setDashboardComercialInformation(undefined);
      throw new Error("Falha ao buscar informações do dashboard!");
    }
  }, [
    getDashboardComercialInformationUseCase,
  ]);

  const loadDocListaState = useCallback(async (funcState?: LoadPrincipalListaFuncState) => {
    try {
      const filteredPage = funcState?.page ?? pagination.page;
      const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;
      await getAllListagemDocUseCase.getAllListagemDoc(
        String(CommonConstants.moduloDocumentos.Comercial),
        filteredPage,
        filteredRowsPerPage
      );
      setDocLista(getAllListagemDocUseCase.items);
      pagination.setRowsPerPage(getAllListagemDocUseCase.pageSize);
      pagination.setTotalItems(getAllListagemDocUseCase.totalItems);
    } catch (e: unknown) {
      throw new Error("Falha ao buscar documentos!");
    }
  }, [
    getAllListagemDocUseCase,
    pagination,
  ]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      await loadDashboardComercialInformation();
      await loadDocListaState();
    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Falha ao carregar os dados. Tente novamente.')[0]);
    }
  });

  /**
   * Eventos
   */

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage(undefined);
      await loadDashboardComercialInformation();
      await loadDocListaState();
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao recarregar os dados. Tente novamente.'));
    } finally {
      setLoading(false);
    }
  }, [
    loadDashboardComercialInformation,
    loadDocListaState,
    pushErrorNotification,
  ]);

  const handleCardClick = useCallback((cardType: string) => {
    switch (cardType) {
      case 'clientesCadastrados':
        useClientesListaFilters.setState({ ...initialClientesListaFiltersValues });
        useClientesListaPagination.setState({ page: 1, });
        backOrNavigate('/Clientes_lista');
        break;

      case 'processosEmAnalise':
        useNovosNegociosListaFilters.setState({ ...initialNovosNegociosFiltersValues, status: 0 });
        useNovosNegociosListaPagination.setState({ page: 1, });
        backOrNavigate('/NovosNegocios_lista');
        break;

      case 'processosDescartados':
        useNovosNegociosListaFilters.setState({ ...initialNovosNegociosFiltersValues, status: 2 });
        useNovosNegociosListaPagination.setState({ page: 1, });
        backOrNavigate('/NovosNegocios_lista');
        break;

      case 'processosParticipados':
        useNovosNegociosListaFilters.setState({ ...initialNovosNegociosFiltersValues, status: 1 });
        useNovosNegociosListaPagination.setState({ page: 1, });
        backOrNavigate('/NovosNegocios_lista');
        break;

      case 'processosParticipadosGanhos':
        useNovosNegociosListaFilters.setState({ ...initialNovosNegociosFiltersValues, status: 3 });
        useNovosNegociosListaPagination.setState({ page: 1, });
        backOrNavigate('/NovosNegocios_lista');
        break;

      case 'processosParticipadosPerdidos':
        useNovosNegociosListaFilters.setState({ ...initialNovosNegociosFiltersValues, status: 4 });
        useNovosNegociosListaPagination.setState({ page: 1, });
        backOrNavigate('/NovosNegocios_lista');
        break;

      case 'processosParticipadosInabilitados':
        useNovosNegociosListaFilters.setState({ ...initialNovosNegociosFiltersValues, status: 6 });
        useNovosNegociosListaPagination.setState({ page: 1, });
        backOrNavigate('/NovosNegocios_lista');
        break;

      case 'processosParticipadosOutros':
        useNovosNegociosListaFilters.setState({ ...initialNovosNegociosFiltersValues, status: 7 });
        useNovosNegociosListaPagination.setState({ page: 1, });
        backOrNavigate('/NovosNegocios_lista');
        break;
    }
  }, [
    backOrNavigate,
  ]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      pagination.setPage(page + 1);;
      await loadDocListaState({ page: page + 1, });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    pagination,
    loadDocListaState,
    pushErrorNotification,
  ]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      pagination.setPage(1);
      pagination.setRowsPerPage(rowsPerPage);

      await loadDocListaState({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    pagination,
    loadDocListaState,
    pushErrorNotification,
  ]);

  const handleDownloadFile = useCallback(async (IdArquivoModulo: number, NomeArquivo: string) => {
    if (IdArquivoModulo > 0) {
      try {
        setLoading(true);
        await getGedArquivoDataUseCase
          .getGedArquivoData(IdArquivoModulo, NomeArquivo);
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar documentos!'));
      } finally {
        setLoading(false);
      }
    }
  }, [
    getGedArquivoDataUseCase,
    pushErrorNotification,
  ]);

  return {
    loading,
    errorMessage,
    dashboardComercialInformation: dashboardComercialInformation ?? null,
    docLista,
    pagination,
    handleReload,
    handleCardClick,
    handlePageChange,
    handleRowsPerPageChange,
    handleDownloadFile,
  };
}